import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Content, Dialog, DialogContainer, Divider, Flex, Heading, Text } from '@adobe/react-spectrum';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { CardTypeIcon } from '../../common/cardStats/CardTypeIcon';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber, formatPct } from '../../common/cardStats/formatters';
import { renderManaText } from '../../common/cardStats/manaText';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { ExpandableRow } from '../../common/expandableRow/ExpandableRow';
import { useExclusiveExpansion } from '../../common/expandableRow/useExclusiveExpansion';

import '../../filterpanel/mana-cost.css';
import './CardsPane.css';

const POWER_CATEGORIES = [
    'tutors',
    'draw',
    'recursion',
    'topdeck',
    'multipliers',
    'fastmana',
    'ramp',
    'costReduction',
    'spotRemoval',
    'boardWipes',
    'counters',
    'stax',
    'taxes',
    'graveyard',
    'evasion',
    'otherControl',
    'discard',
    'combat',
    'extraCombats',
    'extraTurns',
    'overrun',
    'voltron',
    'anthem',
    'burn',
    'mill',
    'poison',
    'tokens',
    'groupslug',
    'grouphug',
    'stompy',
    'wincon',
    'wincon_anthem',
    'wincon_burn',
    'wincon_groupslug',
    'wincon_poison',
    'wincon_stompy',
    'wincon_tokens',
];

const BRACKET_FLAG_META = {
    gameChangers: { label: 'Game Changer', tone: 'alert' },
    massLandDenial: { label: 'Mass Land Denial', tone: 'alert' },
    extraTurns: { label: 'Extra Turn', tone: 'watch' },
    earlyGameInfiniteCombos: { label: 'Early-Game Combo', tone: 'alert' },
    // cedhStaples: { label: 'cEDH Staple', tone: 'watch' },
};

const MANABASE_BUCKET_META = {
    landScores: { label: 'Land' },
    landRampScores: { label: 'Land ramp' },
    dorkScores: { label: 'Mana dork' },
    rockScores: { label: 'Mana rock' },
    ritualScores: { label: 'Ritual' },
    treasureScores: { label: 'Treasure' },
    otherScores: { label: 'Other producer' },
};

const MANA_PRODUCER_TYPE_LABELS = {
    land: 'Land',
    landRamp: 'Land ramp',
    landDork: 'Mana dork',
    dork: 'Mana dork',
    rock: 'Mana rock',
    ritual: 'Ritual',
    treasure: 'Treasure',
    other: 'Other producer',
};

const COLOR_ORDER = ['w', 'u', 'b', 'r', 'g', 'c'];

function manaProducerColorTokens(producer) {
    if (!producer) return '';
    if (producer.isAnyColor) {
        return '{w}{u}{b}{r}{g}';
    }
    const colors = Array.isArray(producer.colors) ? producer.colors : [];
    return COLOR_ORDER.filter((c) => colors.includes(c))
        .map((c) => `{${c}}`)
        .join('');
}

const SYNERGY_CATEGORIES = ['triggers', 'abilities', 'statics', 'replacements'];

function buildSupportMap(synergyList) {
    const map = new Map();
    if (!synergyList) return map;
    Object.entries(synergyList).forEach(([anchorId, node]) => {
        SYNERGY_CATEGORIES.forEach((cat) => {
            const entries = node?.[cat] || {};
            Object.entries(entries).forEach(([entryKey, entry]) => {
                (entry?.sources || []).forEach((src) => {
                    const id = typeof src === 'string' ? src : src?.id;
                    if (!id) return;
                    if (!map.has(id)) map.set(id, []);
                    map.get(id).push({
                        anchorId,
                        category: cat,
                        entryKey,
                        text: entry?.text,
                        kind: 'source',
                        supportText: typeof src === 'object' ? src?.supportCondition?.text : null,
                    });
                });
                (entry?.enablers || []).forEach((en) => {
                    const id = typeof en === 'string' ? en : en?.id;
                    if (!id) return;
                    if (!map.has(id)) map.set(id, []);
                    map.get(id).push({
                        anchorId,
                        category: cat,
                        entryKey,
                        text: entry?.text,
                        kind: 'enabler',
                    });
                });
            });
        });
    });
    return map;
}

function buildComboDisplay(combo, cards) {
    if (!combo) return '';
    const names = (combo.cards || [])
        .map((id) => {
            const c = cards?.[id];
            return c?.name || id;
        })
        .filter(Boolean);
    if (names.length === 0) return combo.name || combo.id || 'Unnamed combo';
    return names.join(' + ');
}

function computeCardDetail(cardId, deck, cards, supportMap) {
    const detail = {
        categories: [],
        powerContribs: [],
        powerTotal: 0,
        bracketFlags: [],
        combos: [],
        synergyAnchor: { entries: 0, score: 0, supporters: 0, enablers: 0, items: [] },
        synergySupports: [],
        commanderSupports: [],
        otherSupports: [],
        outgoingBreakdown: {
            triggers: { source: 0, enabler: 0 },
            abilities: { source: 0, enabler: 0 },
            statics: { source: 0, enabler: 0 },
            replacements: { source: 0, enabler: 0 },
        },
    };

    const scoring = deck?.details?.powerLevel?.scoring || {};
    POWER_CATEGORIES.forEach((cat) => {
        const entry = scoring?.[cat]?.list?.[cardId];
        if (!entry || !(entry.score > 0)) return;
        detail.powerContribs.push({
            key: cat,
            label: getPrettyLabel(cat),
            score: Number(entry.score || 0),
            maxScore: Number(entry.maxScore || 0),
            ratio: Number(entry.scoreToMaxScoreRatio || 0),
            bonus: Number(entry.bonus || 0),
        });
        detail.powerTotal += Number(entry.score || 0);
    });
    detail.powerContribs.sort((a, b) => b.score - a.score);

    const bracketCats = deck?.details?.brackets?.categories || {};
    Object.keys(BRACKET_FLAG_META).forEach((key) => {
        const list = bracketCats?.[key]?.list || [];
        if (list.includes(cardId)) {
            detail.bracketFlags.push({ key, ...BRACKET_FLAG_META[key] });
        }
    });

    const combos = deck?.details?.combos?.list || [];
    combos.forEach((combo) => {
        const ids = combo?.cards || [];
        if (ids.includes(cardId)) {
            const otherIds = ids.filter((id) => id !== cardId);
            detail.combos.push({
                id: combo.id,
                displayName: buildComboDisplay(combo, cards),
                cardIds: ids,
                partnerCards: otherIds.map((id) => ({
                    id,
                    name: cards?.[id]?.name || id,
                    imageUri: cards?.[id]?.imageUri,
                })),
                type: combo.type,
                categories: combo.categories || [],
            });
        }
    });

    const synergyNode = deck?.details?.synergy?.list?.[cardId];
    if (synergyNode) {
        SYNERGY_CATEGORIES.forEach((cat) => {
            const bucket = synergyNode?.[cat] || {};
            Object.entries(bucket).forEach(([entryKey, entry]) => {
                const supporters = entry?.sources?.length || 0;
                const enablers = entry?.enablers?.length || 0;
                const score = Number(entry?.conditionScoring?.total || 0);
                detail.synergyAnchor.entries += 1;
                detail.synergyAnchor.score += score;
                detail.synergyAnchor.supporters += supporters;
                detail.synergyAnchor.enablers += enablers;
                detail.synergyAnchor.items.push({
                    category: cat,
                    entryKey,
                    text: entry?.text,
                    score,
                    supporters,
                    enablers,
                    supporterIds: (entry?.sources || [])
                        .map((s) => (typeof s === 'string' ? s : s?.id))
                        .filter(Boolean),
                    enablerIds: (entry?.enablers || []).map((s) => (typeof s === 'string' ? s : s?.id)).filter(Boolean),
                });
            });
        });
        detail.synergyAnchor.items.sort((a, b) => b.score - a.score);
    }

    const supports = supportMap.get(cardId) || [];
    detail.synergySupports = supports;
    detail.supportSourceCount = supports.filter((s) => s.kind === 'source').length;
    detail.supportEnablerCount = supports.filter((s) => s.kind === 'enabler').length;
    detail.isEnablerHeavy = detail.supportEnablerCount > 0 && detail.supportEnablerCount > detail.supportSourceCount;

    const manabase = deck?.details?.manabase || {};
    const manaMeta = manabase?.manaProducers?.[cardId];
    if (manaMeta) {
        let score = 0;
        const breakdown = manabase?.breakdown || {};
        let bucketKey = null;
        Object.keys(MANABASE_BUCKET_META).forEach((key) => {
            const bucket = breakdown[key];
            if (bucket && bucket[cardId] != null) {
                score += Number(bucket[cardId] || 0);
                if (!bucketKey) bucketKey = key;
            }
        });
        detail.manaProducer = {
            type: manaMeta.type,
            typeLabel:
                MANA_PRODUCER_TYPE_LABELS[manaMeta.type] ||
                (bucketKey ? MANABASE_BUCKET_META[bucketKey].label : 'Producer'),
            colors: Array.isArray(manaMeta.colors) ? COLOR_ORDER.filter((c) => manaMeta.colors.includes(c)) : [],
            isAnyColor: !!manaMeta.isAnyColor,
            quantity: Number(manaMeta.quantity || 0),
            cmc: Number(manaMeta.cmc || 0),
            isFastMana: !!manaMeta.isFastMana,
            isSingleUse: !!manaMeta.isSingleUse,
            doesNotTapToAdd: !!manaMeta.doesNotTapToAdd,
            score,
        };
    }

    const commanderBuckets = new Map();
    const otherBuckets = new Map();
    supports.forEach((sup) => {
        if (detail.outgoingBreakdown[sup.category]) {
            const bucket = detail.outgoingBreakdown[sup.category];
            if (sup.kind === 'enabler') bucket.enabler += 1;
            else bucket.source += 1;
        }
        const anchorCard = cards?.[sup.anchorId];
        const targetMap = anchorCard?.isCommander ? commanderBuckets : otherBuckets;
        if (!targetMap.has(sup.anchorId)) {
            targetMap.set(sup.anchorId, {
                anchorId: sup.anchorId,
                name: anchorCard?.name || sup.anchorId,
                imageUri: anchorCard?.imageUri,
                entries: [],
            });
        }
        targetMap.get(sup.anchorId).entries.push(sup);
    });
    detail.commanderSupports = Array.from(commanderBuckets.values());
    detail.otherSupports = Array.from(otherBuckets.values());

    const cardCategories = cards?.[cardId]?.categories?.stats;
    detail.categories = cardCategories ? Object.keys(cardCategories).filter((k) => k !== 'slow') : [];

    return detail;
}

function buildDeckStats(cardsArray, detailMap) {
    let withPowerScore = 0;
    let comboPieces = 0;
    let synergyAnchors = 0;
    let bracketHits = 0;
    let totalPowerContribution = 0;
    cardsArray.forEach((c) => {
        const d = detailMap.get(c.id);
        if (!d) return;
        if (d.powerTotal > 0) {
            withPowerScore += 1;
            totalPowerContribution += d.powerTotal;
        }
        if (d.combos.length > 0) comboPieces += 1;
        if (d.synergyAnchor.entries > 0) synergyAnchors += 1;
        if (d.bracketFlags.length > 0) bracketHits += 1;
    });
    return {
        total: cardsArray.length,
        withPowerScore,
        comboPieces,
        synergyAnchors,
        bracketHits,
        avgPowerContribution: withPowerScore > 0 ? totalPowerContribution / withPowerScore : 0,
    };
}

function buildRoleChips(cardsArray, cards) {
    const counts = new Map();
    cardsArray.forEach((c) => {
        const stats = cards?.[c.id]?.categories?.stats;
        if (!stats) return;
        Object.keys(stats).forEach((key) => {
            if (key === 'slow') return;
            counts.set(key, (counts.get(key) || 0) + 1);
        });
    });
    return Array.from(counts.entries())
        .map(([key, count]) => ({ key, label: getPrettyLabel(key), count }))
        .sort((a, b) => b.count - a.count);
}

export function CardsPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const { expandedId, toggle: toggleExpanded } = useExclusiveExpansion();
    const [activeRole, setActiveRole] = useState(null);
    const [mindMapCardId, setMindMapCardId] = useState(null);
    const openCardInfo = useOpenCardInfo();

    const supportMap = useMemo(() => buildSupportMap(deck?.details?.synergy?.list), [deck?.details?.synergy?.list]);

    const cardArray = useMemo(() => {
        // Include every face — back faces (MDFCs, transforms) contribute
        // to the deck individually (their own salt / scoring / categories)
        // so filtering them out hides real signal. Rendering the back
        // alongside the front is how Harnfel, Horn of Bounty shows up
        // next to Birgi, God of Storytelling.
        const arr = Object.values(cards || {})
            .filter((c) => c && c.id)
            .map((c) => ({ ...c }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return arr;
    }, [cards]);

    const detailMap = useMemo(() => {
        const map = new Map();
        cardArray.forEach((c) => {
            map.set(c.id, computeCardDetail(c.id, deck, cards, supportMap));
        });
        return map;
    }, [cardArray, deck, cards, supportMap]);

    const deckStats = useMemo(() => buildDeckStats(cardArray, detailMap), [cardArray, detailMap]);

    const roleChips = useMemo(() => buildRoleChips(cardArray, cards), [cardArray, cards]);

    const filteredCards = useMemo(() => {
        if (!activeRole) return cardArray;
        return cardArray.filter((c) => !!cards?.[c.id]?.categories?.stats?.[activeRole]);
    }, [cardArray, activeRole, cards]);

    const sortOptions = useMemo(() => {
        const getDetail = (card) => detailMap.get(card?.id);
        const commanderSupportCount = (card) =>
            (getDetail(card)?.commanderSupports || []).reduce((s, c) => s + c.entries.length, 0);
        return [
            {
                key: 'name',
                label: 'Name',
                isText: true,
                defaultDir: 'asc',
                getValue: (card) => card?.name || '',
            },
            {
                key: 'power',
                label: 'Power contribution',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.powerTotal || 0,
            },
            {
                key: 'synergy-score',
                label: 'Synergy anchor score',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.synergyAnchor?.score || 0,
            },
            {
                key: 'commander-support',
                label: 'Commander support',
                defaultDir: 'desc',
                getValue: commanderSupportCount,
            },
            {
                key: 'supports',
                label: 'Supports other cards (total)',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.synergySupports?.length || 0,
            },
            {
                key: 'sources',
                label: 'Source relationships',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.supportSourceCount || 0,
            },
            {
                key: 'enablers',
                label: 'Enabler relationships',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.supportEnablerCount || 0,
            },
            {
                key: 'combos',
                label: 'Combo pieces',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.combos?.length || 0,
            },
            {
                key: 'categories',
                label: 'Category coverage',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.categories?.length || 0,
            },
            {
                key: 'bracket',
                label: 'Bracket flags',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.bracketFlags?.length || 0,
            },
            {
                key: 'mana-score',
                label: 'Manabase score',
                defaultDir: 'desc',
                getValue: (card) => getDetail(card)?.manaProducer?.score || 0,
            },
        ];
    }, [detailMap]);

    const handlePreview = (card) => {
        if (!card) return;
        openCardInfo(card);
    };

    return (
        <Flex direction="column" UNSAFE_className="cardsPane" rowGap={18} padding="12px">
            <div className="statsMetricsGrid">
                <StatMetricTile label="Cards" value={formatInt(deckStats.total)} hint="All front faces in the list" />
                <StatMetricTile
                    label="Power-scored"
                    value={formatInt(deckStats.withPowerScore)}
                    hint={`${formatNumber(deckStats.avgPowerContribution, 1)} avg points`}
                />
                <StatMetricTile
                    label="Combo pieces"
                    value={formatInt(deckStats.comboPieces)}
                    hint="Appear in at least one combo"
                />
                <StatMetricTile
                    label="Synergy anchors"
                    value={formatInt(deckStats.synergyAnchors)}
                    hint="Anchor at least one synergy effect"
                />
            </div>

            {roleChips.length > 0 && (
                <StatsSection
                    title="Filter by role"
                    subtitle="Narrow the list to a specific role — clears the selection to show everything"
                >
                    <div className="cardsPaneRoleChips">
                        <button
                            type="button"
                            className={`cardsPaneRoleChip${!activeRole ? ' is-active' : ''}`}
                            onClick={() => setActiveRole(null)}
                        >
                            <span className="cardsPaneRoleChipLabel">All</span>
                            <span className="cardsPaneRoleChipCount">{cardArray.length}</span>
                        </button>
                        {roleChips.map((r) => (
                            <button
                                type="button"
                                key={r.key}
                                className={`cardsPaneRoleChip${activeRole === r.key ? ' is-active' : ''}`}
                                onClick={() => setActiveRole((prev) => (prev === r.key ? null : r.key))}
                                title={`${r.count} cards tagged ${r.label}`}
                            >
                                <span className="cardsPaneRoleChipLabel">{r.label}</span>
                                <span className="cardsPaneRoleChipCount">{r.count}</span>
                            </button>
                        ))}
                    </div>
                </StatsSection>
            )}

            <CardStatList
                cards={filteredCards}
                deckCount={1}
                onSelectCard={handlePreview}
                resetKey={`cardsPane-${activeRole || 'all'}`}
                initialVisible={30}
                expandStep={40}
                searchPlaceholder="Filter cards by name…"
                sortOptions={sortOptions}
                initialSortKey="name"
                sortLabel="Sort cards"
                compactFilters
                renderCard={(card) => {
                    const detail = detailMap.get(card.id);
                    const rowKey = `card:${card.id}`;
                    return (
                        <CardDeepDiveRow
                            key={card.id}
                            card={card}
                            detail={detail}
                            isExpanded={expandedId === rowKey}
                            onToggle={() => toggleExpanded(rowKey)}
                            onPreview={handlePreview}
                            onOpenMindMap={() => setMindMapCardId(card.id)}
                            cardsMap={cards}
                        />
                    );
                }}
                emptyMessageUnfiltered="This deck has no cards yet."
            />
            {mindMapCardId && (
                <MindMapDialog
                    card={cards?.[mindMapCardId]}
                    detail={detailMap.get(mindMapCardId)}
                    cards={cards}
                    onClose={() => setMindMapCardId(null)}
                />
            )}
        </Flex>
    );
}

function CardDeepDiveRow({ card, detail, isExpanded, onToggle, onPreview, onOpenMindMap, cardsMap }) {
    if (!card) return null;
    const d = detail || {};
    const hasPower = (d.powerContribs || []).length > 0;
    const hasBracket = (d.bracketFlags || []).length > 0;
    const hasCombos = (d.combos || []).length > 0;
    const hasSynergyAnchor = (d.synergyAnchor?.entries || 0) > 0;
    const hasSynergySupports = (d.synergySupports || []).length > 0;
    const hasCategories = (d.categories || []).length > 0;
    const hasCommanderBond = (d.commanderSupports || []).length > 0;
    const hasMana = !!d.manaProducer;
    const commanderBondCount = (d.commanderSupports || []).reduce((s, c) => s + c.entries.length, 0);

    const maxPowerScore = (d.powerContribs || []).reduce((m, p) => Math.max(m, p.score), 1);

    const header = (
        <>
            {card.imageUri ? (
                <img
                    src={card.imageUri}
                    alt={card.name}
                    className="cardStatThumb"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPreview?.(card);
                    }}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <div className="cardRowTitle">
                <div className="cardRowTitleTop">
                    <CardTypeIcon types={card.types} />
                    <span className="cardRowName">{card.name}</span>
                </div>
                <div className="cardRowBadges">
                    {hasPower && (
                        <span className="cardRowBadge cardRowBadge--gold" title="Total power-level contribution">
                            {formatInt(d.powerTotal)} pwr
                        </span>
                    )}
                    {hasBracket &&
                        d.bracketFlags.map((f) => (
                            <span
                                key={f.key}
                                className={`cardRowBadge cardRowBadge--${f.tone}`}
                                title={`Pushes the deck's bracket via ${f.label}`}
                            >
                                {f.label}
                            </span>
                        ))}
                    {hasCombos && (
                        <span
                            className="cardRowBadge cardRowBadge--combo"
                            title={`Part of ${d.combos.length} combo${d.combos.length === 1 ? '' : 's'}`}
                        >
                            {d.combos.length} combo{d.combos.length === 1 ? '' : 's'}
                        </span>
                    )}
                    {hasSynergyAnchor && (
                        <span
                            className="cardRowBadge cardRowBadge--synergy"
                            title={`Anchors ${d.synergyAnchor.entries} synergy effect${
                                d.synergyAnchor.entries === 1 ? '' : 's'
                            }`}
                        >
                            {formatInt(d.synergyAnchor.score)} syn
                        </span>
                    )}
                    {(d.supportSourceCount || 0) > 0 && (
                        <span
                            className="cardRowBadge cardRowBadge--support"
                            title={`Source for ${d.supportSourceCount} other effect${
                                d.supportSourceCount === 1 ? '' : 's'
                            }`}
                        >
                            {d.supportSourceCount} src
                        </span>
                    )}
                    {(d.supportEnablerCount || 0) > 0 && (
                        <span
                            className="cardRowBadge cardRowBadge--enabler"
                            title={`Enables ${d.supportEnablerCount} other effect${
                                d.supportEnablerCount === 1 ? '' : 's'
                            }`}
                        >
                            {d.supportEnablerCount} ena
                        </span>
                    )}
                    {d.isEnablerHeavy && (
                        <span
                            className="cardRowBadge cardRowBadge--enablerHeavy"
                            title="Enabler-heavy card — contributes more as an enabler than as a source"
                        >
                            Enabler-heavy
                        </span>
                    )}
                    {hasCommanderBond && (
                        <span
                            className="cardRowBadge cardRowBadge--commander"
                            title={`Feeds or enables ${commanderBondCount} commander effect${
                                commanderBondCount === 1 ? '' : 's'
                            }`}
                        >
                            {commanderBondCount} cmd
                        </span>
                    )}
                    {hasMana && (
                        <span
                            className="cardRowBadge cardRowBadge--mana"
                            title={`Manabase contribution: ${d.manaProducer.typeLabel} · ${formatNumber(
                                d.manaProducer.score || 0,
                                1
                            )} pts`}
                        >
                            {d.manaProducer.typeLabel}
                            {d.manaProducer.score > 0 ? ` ${formatNumber(d.manaProducer.score, 0)}` : ''}
                        </span>
                    )}
                </div>
            </div>
        </>
    );

    return (
        <ExpandableRow
            expanded={isExpanded}
            onToggle={onToggle}
            tone="red"
            rowClassName="cardRow"
            headerClassName="cardRowHeader"
            bodyClassName="cardRowBody"
            ariaLabel={`${isExpanded ? 'Collapse' : 'Expand'} details for ${card.name}`}
            header={header}
        >
                    {hasCategories && (
                        <DetailBlock label="Roles">
                            <div className="cardsPaneInlineChips">
                                {d.categories.map((cat) => (
                                    <span key={cat} className="cardsPaneInlineChip">
                                        {getPrettyLabel(cat)}
                                    </span>
                                ))}
                            </div>
                        </DetailBlock>
                    )}

                    {hasPower && (
                        <DetailBlock
                            label="Power-level contribution"
                            hint={`${formatInt(d.powerTotal)} total points across ${d.powerContribs.length} categor${
                                d.powerContribs.length === 1 ? 'y' : 'ies'
                            }`}
                        >
                            <div className="cardsPanePowerList">
                                {d.powerContribs.map((p) => {
                                    const barPct = Math.max(2, Math.round((p.score / maxPowerScore) * 100));
                                    const maxPct = p.maxScore > 0 ? Math.round((p.score / p.maxScore) * 100) : null;
                                    return (
                                        <div className="cardsPanePowerRow" key={p.key}>
                                            <span className="cardsPanePowerLabel">{p.label}</span>
                                            <div className="cardsPanePowerBarTrack">
                                                <div
                                                    className="cardsPanePowerBarFill"
                                                    style={{ width: `${barPct}%` }}
                                                />
                                            </div>
                                            <span className="cardsPanePowerValue">
                                                {formatNumber(p.score, 1)}
                                                {p.maxScore > 0 && (
                                                    <span className="cardsPanePowerValueMax">
                                                        {` / ${formatNumber(p.maxScore, 0)}`}
                                                    </span>
                                                )}
                                            </span>
                                            {maxPct != null && (
                                                <span className="cardsPanePowerRatio">
                                                    {formatPct(Math.min(maxPct / 100, 1))}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </DetailBlock>
                    )}

                    {hasBracket && (
                        <DetailBlock label="Bracket influence" hint="Flags that push the deck's bracket upward">
                            <div className="cardsPaneInlineChips">
                                {d.bracketFlags.map((f) => (
                                    <span
                                        key={f.key}
                                        className={`cardsPaneBracketChip cardsPaneBracketChip--${f.tone}`}
                                    >
                                        {f.label}
                                    </span>
                                ))}
                            </div>
                        </DetailBlock>
                    )}

                    {hasCombos && (
                        <DetailBlock
                            label={`Combo pieces (${d.combos.length})`}
                            hint={
                                d.combos.length > 1
                                    ? 'Redundant combo membership increases reliability'
                                    : 'Card participates in one combo line'
                            }
                        >
                            <div className="cardsPaneComboList">
                                {d.combos.map((combo) => (
                                    <div className="cardsPaneComboRow" key={combo.id}>
                                        <div className="cardsPaneComboNameBlock">
                                            <span className="cardsPaneComboName">{combo.displayName}</span>
                                            {combo.partnerCards.length > 0 && (
                                                <span className="cardsPaneComboPartners">
                                                    with {combo.partnerCards.map((p) => p.name).join(' + ')}
                                                </span>
                                            )}
                                        </div>
                                        <span className="cardsPaneComboMeta">
                                            {combo.cardIds.length} card
                                            {combo.cardIds.length === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </DetailBlock>
                    )}

                    {hasSynergyAnchor && (
                        <DetailBlock
                            label={`Synergy anchor (${d.synergyAnchor.entries} effect${
                                d.synergyAnchor.entries === 1 ? '' : 's'
                            })`}
                            hint={`${formatInt(d.synergyAnchor.score)} total synergy pts · ${
                                d.synergyAnchor.supporters
                            } supporters · ${d.synergyAnchor.enablers} enablers`}
                        >
                            <div className="cardsPaneSynergyAnchorList">
                                {d.synergyAnchor.items.slice(0, 6).map((item) => (
                                    <div
                                        className="cardsPaneSynergyAnchorItem"
                                        key={`${item.category}::${item.entryKey}`}
                                    >
                                        <span className="cardsPaneSynergyAnchorCat">{item.category}</span>
                                        <span className="cardsPaneSynergyAnchorText">
                                            &ldquo;{renderManaText(item.text || item.entryKey)}&rdquo;
                                        </span>
                                        <span className="cardsPaneSynergyAnchorScore">
                                            {formatNumber(item.score, 1)}
                                        </span>
                                        <span className="cardsPaneSynergyAnchorMeta">
                                            {item.supporters}s / {item.enablers}e
                                        </span>
                                    </div>
                                ))}
                                {d.synergyAnchor.items.length > 6 && (
                                    <span className="cardsPaneSynergyOverflow">
                                        +{d.synergyAnchor.items.length - 6} more — check the synergy tab for the full
                                        drilldown
                                    </span>
                                )}
                            </div>
                        </DetailBlock>
                    )}

                    {hasCommanderBond && (
                        <DetailBlock
                            label={`Commander bond (${commanderBondCount} effect${
                                commanderBondCount === 1 ? '' : 's'
                            })`}
                            hint="How this card feeds or enables your commander(s)"
                        >
                            <div className="cardsPaneCommanderBondList">
                                {d.commanderSupports.map((cmd) => (
                                    <div className="cardsPaneCommanderBond" key={cmd.anchorId}>
                                        {cmd.imageUri ? (
                                            <img
                                                src={cmd.imageUri}
                                                alt={cmd.name}
                                                className="cardsPaneCommanderImg"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="cardsPaneCommanderImgPh" />
                                        )}
                                        <div className="cardsPaneCommanderBondMeta">
                                            <span className="cardsPaneCommanderBondName">{cmd.name}</span>
                                            <span className="cardsPaneCommanderBondCount">
                                                {cmd.entries.length} effect
                                                {cmd.entries.length === 1 ? '' : 's'}
                                            </span>
                                            <div className="cardsPaneCommanderBondEffects">
                                                {cmd.entries.slice(0, 4).map((e, idx) => (
                                                    <span
                                                        className="cardsPaneCommanderBondEffect"
                                                        key={`${e.category}::${e.entryKey}::${idx}`}
                                                    >
                                                        <span className="cardsPaneCommanderBondKind">
                                                            {e.kind === 'enabler' ? 'enabler' : 'source'}
                                                        </span>
                                                        <span className="cardsPaneCommanderBondText">
                                                            &ldquo;{renderManaText(e.text || '')}&rdquo;
                                                        </span>
                                                    </span>
                                                ))}
                                                {cmd.entries.length > 4 && (
                                                    <span className="cardsPaneSynergyOverflow">
                                                        +{cmd.entries.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </DetailBlock>
                    )}

                    {hasSynergySupports && (
                        <DetailBlock
                            label={`Outgoing impact (${d.synergySupports.length})`}
                            hint="What this card enables — broken down by effect type, plus the top anchors it feeds"
                        >
                            <div className="cardsPaneOutgoingGrid">
                                {['triggers', 'abilities', 'statics', 'replacements'].map((cat) => {
                                    const counts = d.outgoingBreakdown?.[cat] || {
                                        source: 0,
                                        enabler: 0,
                                    };
                                    const total = counts.source + counts.enabler;
                                    if (total === 0) return null;
                                    return (
                                        <div
                                            className={`cardsPaneOutgoingTile cardsPaneOutgoingTile--${cat}`}
                                            key={cat}
                                        >
                                            <span className="cardsPaneOutgoingLabel">{cat}</span>
                                            <span className="cardsPaneOutgoingCount">{total}</span>
                                            <span className="cardsPaneOutgoingMeta">
                                                {counts.source} src · {counts.enabler} ena
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="cardsPaneSupportList cardsPaneSupportListScroll">
                                {(d.otherSupports || []).map((grp) => (
                                    <div className="cardsPaneSupportItem" key={grp.anchorId}>
                                        <span className="cardsPaneSupportAnchor">{grp.name}</span>
                                        <span className="cardsPaneSupportKind">
                                            {grp.entries.length} effect
                                            {grp.entries.length === 1 ? '' : 's'}
                                        </span>
                                        <span className="cardsPaneSupportText">
                                            &ldquo;{renderManaText(grp.entries[0]?.text || '')}&rdquo;
                                            {grp.entries.length > 1 && ` · +${grp.entries.length - 1} more`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </DetailBlock>
                    )}

                    {hasMana && (
                        <DetailBlock label="Manabase contribution" hint="How this card feeds the deck's manabase score">
                            <div className="cardsPaneManaBlock">
                                <div className="cardsPaneManaHead">
                                    <span className="cardsPaneManaType">{d.manaProducer.typeLabel}</span>
                                    {d.manaProducer.score > 0 && (
                                        <span className="cardsPaneManaScore">
                                            {formatNumber(d.manaProducer.score, 1)} pts
                                        </span>
                                    )}
                                </div>
                                <div className="cardsPaneManaMeta">
                                    {(d.manaProducer.colors.length > 0 || d.manaProducer.isAnyColor) && (
                                        <span className="cardsPaneManaMetaItem">
                                            <span className="cardsPaneManaMetaLabel">Produces</span>
                                            <span className="cardsPaneManaColors">
                                                {d.manaProducer.quantity > 1 && (
                                                    <span className="cardsPaneManaQuantity">
                                                        {d.manaProducer.quantity}×
                                                    </span>
                                                )}
                                                {renderManaText(manaProducerColorTokens(d.manaProducer), 'medium')}
                                                {d.manaProducer.isAnyColor && (
                                                    <span className="cardsPaneManaAnyLabel">any</span>
                                                )}
                                            </span>
                                        </span>
                                    )}
                                    <span className="cardsPaneManaMetaItem">
                                        <span className="cardsPaneManaMetaLabel">CMC</span>
                                        <span className="cardsPaneManaMetaValue">{d.manaProducer.cmc}</span>
                                    </span>
                                </div>
                                <div className="cardsPaneManaFlags">
                                    {d.manaProducer.isFastMana && (
                                        <span className="cardsPaneManaFlag cardsPaneManaFlag--fast">Fast mana</span>
                                    )}
                                    {d.manaProducer.isSingleUse && (
                                        <span className="cardsPaneManaFlag">Single use</span>
                                    )}
                                    {d.manaProducer.doesNotTapToAdd && (
                                        <span className="cardsPaneManaFlag">Doesn't tap</span>
                                    )}
                                </div>
                            </div>
                        </DetailBlock>
                    )}

                    {!hasPower &&
                        !hasBracket &&
                        !hasCombos &&
                        !hasSynergyAnchor &&
                        !hasSynergySupports &&
                        !hasCategories &&
                        !hasMana && (
                            <Text UNSAFE_className="cardsPaneEmpty">
                                No contribution signal recorded for this card.
                            </Text>
                        )}
        </ExpandableRow>
    );
}

function DetailBlock({ label, hint, children }) {
    return (
        <div className="cardsPaneDetailBlock">
            <div className="cardsPaneDetailHeader">
                <span className="cardsPaneDetailLabel">{label}</span>
                {hint && <span className="cardsPaneDetailHint">{hint}</span>}
            </div>
            {children}
        </div>
    );
}

function MindMapDialog({ card, detail, cards, onClose }) {
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [hoveredNodeId, setHoveredNodeId] = useState(null);

    const nodes = useMemo(() => {
        if (!card || !detail) return [];
        const list = [];
        const seen = new Set();
        const push = (node) => {
            if (!node || !node.id || seen.has(node.id)) return;
            seen.add(node.id);
            list.push(node);
        };

        (detail.commanderSupports || []).slice(0, 3).forEach((c) => {
            push({
                id: c.anchorId,
                name: c.name,
                imageUri: c.imageUri,
                color: '#ff6ea8',
                kind: 'commander',
                kindLabel: 'Commander bond',
                detail: `${c.entries.length} effect${c.entries.length === 1 ? '' : 's'}`,
                effects: c.entries.map((e) => ({
                    text: e.text || '',
                    kind: e.kind,
                    category: e.category,
                })),
            });
        });

        const combosByPartner = new Map();
        (detail.combos || []).forEach((combo) => {
            combo.partnerCards.forEach((p) => {
                if (!combosByPartner.has(p.id)) combosByPartner.set(p.id, []);
                combosByPartner.get(p.id).push({
                    displayName: combo.displayName,
                    type: combo.type,
                    categories: combo.categories || [],
                });
            });
        });
        (detail.combos || []).forEach((combo) => {
            combo.partnerCards.forEach((p) => {
                push({
                    id: p.id,
                    name: p.name,
                    imageUri: p.imageUri,
                    color: '#b87be8',
                    kind: 'combo',
                    kindLabel: 'Combo partner',
                    detail: 'combo partner',
                    effects: (combosByPartner.get(p.id) || []).map((combo) => ({
                        text: combo.displayName,
                        kind: combo.type || 'combo',
                        category: (combo.categories || []).join(' · '),
                    })),
                });
            });
        });

        const incomingByCard = new Map();
        (detail.synergyAnchor?.items || []).forEach((item) => {
            (item.supporterIds || []).forEach((id) => {
                if (!incomingByCard.has(id)) incomingByCard.set(id, []);
                incomingByCard.get(id).push({
                    text: item.text || '',
                    kind: 'source',
                    category: item.category,
                });
            });
            (item.enablerIds || []).forEach((id) => {
                if (!incomingByCard.has(id)) incomingByCard.set(id, []);
                incomingByCard.get(id).push({
                    text: item.text || '',
                    kind: 'enabler',
                    category: item.category,
                });
            });
        });
        Array.from(incomingByCard.entries())
            .sort((a, b) => b[1].length - a[1].length)
            .slice(0, 6)
            .forEach(([id, effects]) => {
                const c = cards?.[id];
                if (!c) return;
                push({
                    id,
                    name: c.name || id,
                    imageUri: c.imageUri,
                    color: '#cc1a1a',
                    kind: 'incoming',
                    kindLabel: 'Feeds this card',
                    detail: `feeds ${effects.length}`,
                    effects,
                });
            });

        (detail.otherSupports || []).slice(0, 6).forEach((grp) => {
            push({
                id: grp.anchorId,
                name: grp.name,
                imageUri: grp.imageUri,
                color: '#5fa85a',
                kind: 'outgoing',
                kindLabel: 'This card feeds',
                detail: `helps ${grp.entries.length}`,
                effects: grp.entries.map((e) => ({
                    text: e.text || '',
                    kind: e.kind,
                    category: e.category,
                })),
            });
        });

        return list.slice(0, 16);
    }, [card, detail, cards]);

    if (!card) return null;

    const centerX = 420;
    const centerY = 300;
    const radius = 220;
    const count = nodes.length;
    const positioned = nodes.map((n, i) => {
        const angle = count > 0 ? -Math.PI / 2 + (i / count) * 2 * Math.PI : 0;
        return {
            ...n,
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle),
        };
    });

    const focusNodeId = selectedNodeId || hoveredNodeId;
    const focusNode = focusNodeId ? positioned.find((n) => n.id === focusNodeId) || null : null;
    const anyFocus = !!focusNode;

    const dimFor = (nodeId) => (!anyFocus ? 1 : nodeId === focusNode.id ? 1 : 0.15);

    return (
        <DialogContainer onDismiss={onClose}>
            <Dialog width="calc(100vw - 40px)" size="L" UNSAFE_className="mindMapDialog">
                <Heading>
                    <span className="mindMapEyebrow">Card mind map · POC</span>
                    <span className="mindMapTitle">{card.name}</span>
                </Heading>
                <button
                    type="button"
                    className="mindMapCloseBtn"
                    onClick={onClose}
                    aria-label="Close mind map"
                    title="Close"
                >
                    ✕
                </button>
                <Divider />
                <Content>
                    <div className="mindMapWrap">
                        {count === 0 ? (
                            <Text UNSAFE_className="mindMapEmpty">No connections to map for this card.</Text>
                        ) : (
                            <div className="mindMapCanvas">
                                <svg
                                    viewBox="0 0 840 600"
                                    className="mindMapSvg"
                                    aria-label="Card mind map"
                                    onClick={() => setSelectedNodeId(null)}
                                >
                                    <defs>
                                        <clipPath id="mindMapCenterClip">
                                            <circle cx={centerX} cy={centerY} r={60} />
                                        </clipPath>
                                        {positioned.map((n, i) => (
                                            <clipPath key={`clip_${i}`} id={`mindMapClip_${i}`}>
                                                <circle cx={n.x} cy={n.y} r={28} />
                                            </clipPath>
                                        ))}
                                    </defs>

                                    {positioned.map((n, i) => {
                                        const isFocus = focusNode?.id === n.id;
                                        const opacity = !anyFocus ? 0.45 : isFocus ? 0.95 : 0.08;
                                        return (
                                            <line
                                                key={`line_${i}`}
                                                x1={centerX}
                                                y1={centerY}
                                                x2={n.x}
                                                y2={n.y}
                                                stroke={n.color}
                                                strokeWidth={isFocus ? 2.5 : 1.5}
                                                strokeOpacity={opacity}
                                            />
                                        );
                                    })}

                                    <g opacity={1}>
                                        <circle
                                            cx={centerX}
                                            cy={centerY}
                                            r={68}
                                            fill="#0a0a0a"
                                            stroke="#e8c600"
                                            strokeWidth={3}
                                        />
                                        {card.imageUri && (
                                            <image
                                                clipPath="url(#mindMapCenterClip)"
                                                href={card.imageUri}
                                                x={centerX - 60}
                                                y={centerY - 60}
                                                width={120}
                                                height={120}
                                                preserveAspectRatio="xMidYMid slice"
                                            />
                                        )}
                                        <text
                                            x={centerX}
                                            y={centerY + 88}
                                            textAnchor="middle"
                                            className="mindMapCenterLabel"
                                        >
                                            {card.name}
                                        </text>
                                    </g>

                                    {positioned.map((n, i) => {
                                        const isFocus = focusNode?.id === n.id;
                                        return (
                                            <g
                                                key={`node_${i}`}
                                                style={{
                                                    opacity: dimFor(n.id),
                                                    cursor: 'pointer',
                                                    transition: 'opacity 0.15s ease',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedNodeId((prev) => (prev === n.id ? null : n.id));
                                                }}
                                                onMouseEnter={() => setHoveredNodeId(n.id)}
                                                onMouseLeave={() => setHoveredNodeId(null)}
                                            >
                                                <circle
                                                    cx={n.x}
                                                    cy={n.y}
                                                    r={isFocus ? 36 : 32}
                                                    fill="#0a0a0a"
                                                    stroke={n.color}
                                                    strokeWidth={isFocus ? 3 : 2}
                                                />
                                                {n.imageUri && (
                                                    <image
                                                        clipPath={`url(#mindMapClip_${i})`}
                                                        href={n.imageUri}
                                                        x={n.x - 28}
                                                        y={n.y - 28}
                                                        width={56}
                                                        height={56}
                                                        preserveAspectRatio="xMidYMid slice"
                                                    />
                                                )}
                                                <text
                                                    x={n.x}
                                                    y={n.y + 48}
                                                    textAnchor="middle"
                                                    className="mindMapNodeName"
                                                >
                                                    {n.name.length > 22 ? `${n.name.slice(0, 21)}…` : n.name}
                                                </text>
                                                <text
                                                    x={n.x}
                                                    y={n.y + 62}
                                                    textAnchor="middle"
                                                    fill={n.color}
                                                    className="mindMapNodeMeta"
                                                >
                                                    {n.detail}
                                                </text>
                                            </g>
                                        );
                                    })}
                                </svg>

                                {focusNode && (
                                    <div
                                        className="mindMapDetail"
                                        style={{
                                            borderLeftColor: focusNode.color,
                                        }}
                                    >
                                        <div className="mindMapDetailHead">
                                            <span className="mindMapDetailKind" style={{ color: focusNode.color }}>
                                                {focusNode.kindLabel || focusNode.kind}
                                            </span>
                                            <span className="mindMapDetailName">{focusNode.name}</span>
                                        </div>
                                        {(focusNode.effects || []).length === 0 ? (
                                            <span className="mindMapDetailEmpty">
                                                No extra context recorded for this connection.
                                            </span>
                                        ) : (
                                            <div className="mindMapDetailList">
                                                {focusNode.effects.slice(0, 8).map((e, idx) => (
                                                    <div className="mindMapDetailEffect" key={`eff_${idx}`}>
                                                        <span
                                                            className="mindMapDetailEffectKind"
                                                            style={{ color: focusNode.color }}
                                                        >
                                                            {e.kind}
                                                            {e.category ? ` · ${e.category}` : ''}
                                                        </span>
                                                        <span className="mindMapDetailEffectText">
                                                            &ldquo;{renderManaText(e.text)}&rdquo;
                                                        </span>
                                                    </div>
                                                ))}
                                                {focusNode.effects.length > 8 && (
                                                    <span className="mindMapDetailOverflow">
                                                        +{focusNode.effects.length - 8} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mindMapLegend">
                            <span className="mindMapLegendItem">
                                <span className="mindMapLegendDot" style={{ background: '#ff6ea8' }} />
                                Commander bonds
                            </span>
                            <span className="mindMapLegendItem">
                                <span className="mindMapLegendDot" style={{ background: '#b87be8' }} />
                                Combo partners
                            </span>
                            <span className="mindMapLegendItem">
                                <span className="mindMapLegendDot" style={{ background: '#cc1a1a' }} />
                                Cards feeding this card
                            </span>
                            <span className="mindMapLegendItem">
                                <span className="mindMapLegendDot" style={{ background: '#5fa85a' }} />
                                Cards this card feeds
                            </span>
                            <span className="mindMapLegendHint">
                                Click a node to pin its details · hover to peek · click empty space to clear
                            </span>
                        </div>
                    </div>
                </Content>
            </Dialog>
        </DialogContainer>
    );
}
